export type AppClient = 'customer' | 'admin';

export type SessionUser = {
  id: number;
  name: string;
  phone: string;
  role: string;
  status?: string;
  wallet_balance?: number | string;
  game_wallet_balance?: number | string;
  games_wallet_balance?: number | string;
  membership_id?: number | null;
  membership_name?: string | null;
  [key: string]: unknown;
};

/** Legacy shared keys (pre-split). Migrated once into per-client keys. */
const LEGACY_TOKEN_KEY = 'eshhanle_token';
const LEGACY_USER_KEY = 'eshhanle_user';

function tokenKey(client: AppClient = detectAppMode()): string {
  return `eshhanle_token_${client}`;
}

function userKey(client: AppClient = detectAppMode()): string {
  return `eshhanle_user_${client}`;
}

function migrateLegacyIfNeeded(client: AppClient): void {
  try {
    if (localStorage.getItem(tokenKey(client))) return;
    const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
    if (!legacy) return;
    // Only adopt legacy token for the current mode; leave it for the other
    // mode until that side boots (or clear after both have migrated).
    localStorage.setItem(tokenKey(client), legacy);
    const legacyUser = localStorage.getItem(LEGACY_USER_KEY);
    if (legacyUser && !localStorage.getItem(userKey(client))) {
      localStorage.setItem(userKey(client), legacyUser);
    }
  } catch {
    // ignore storage failures
  }
}

export function getToken(client?: AppClient): string | null {
  try {
    const mode = client ?? detectAppMode();
    migrateLegacyIfNeeded(mode);
    return localStorage.getItem(tokenKey(mode));
  } catch {
    return null;
  }
}

export function setToken(token: string, client?: AppClient): void {
  const mode = client ?? detectAppMode();
  localStorage.setItem(tokenKey(mode), token);
}

export function clearToken(client?: AppClient): void {
  const mode = client ?? detectAppMode();
  localStorage.removeItem(tokenKey(mode));
  // Drop legacy shared key so it cannot overwrite the other app's session.
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    // ignore
  }
}

export function getCachedUser(client?: AppClient): SessionUser | null {
  try {
    const mode = client ?? detectAppMode();
    migrateLegacyIfNeeded(mode);
    const raw = localStorage.getItem(userKey(mode));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as SessionUser;
  } catch {
    return null;
  }
}

export function setCachedUser(user: SessionUser, client?: AppClient): void {
  const mode = client ?? detectAppMode();
  localStorage.setItem(userKey(mode), JSON.stringify(user));
}

export function clearCachedUser(client?: AppClient): void {
  const mode = client ?? detectAppMode();
  localStorage.removeItem(userKey(mode));
  try {
    localStorage.removeItem(LEGACY_USER_KEY);
  } catch {
    // ignore
  }
}

export function clearSession(client?: AppClient): void {
  clearToken(client);
  clearCachedUser(client);
}

export function setSession(token: string, user: SessionUser, client?: AppClient): void {
  setToken(token, client);
  setCachedUser(user, client);
}

export function detectAppMode(pathname = window.location.pathname): AppClient {
  return pathname.startsWith('/admin') ? 'admin' : 'customer';
}

export function deviceName(): string {
  try {
    const ua = navigator.userAgent || 'web';
    return ua.slice(0, 80);
  } catch {
    return 'web';
  }
}

/** True when API rejected the session (not network / server / schema errors). */
export function isAuthFailure(payload: {
  success?: boolean;
  error?: unknown;
  code?: unknown;
  http_code?: unknown;
  status?: unknown;
}): boolean {
  if (payload.success) return false;
  const code = typeof payload.code === 'string' ? payload.code : '';
  if (code === 'session_revoked' || code === 'unauthorized') return true;
  const err = typeof payload.error === 'string' ? payload.error : '';
  if (
    err.includes('تسجيل الدخول مطلوب') ||
    err.includes('جلسة غير صالحة') ||
    err.includes('انتهت الجلسة') ||
    err.includes('تم إنهاء هذه الجلسة') ||
    err.includes('لا تخص نسخة التطبيق')
  ) {
    return true;
  }
  const http = Number(payload.http_code ?? payload.status ?? 0);
  return http === 401;
}
